/**
 * Product Description Image Manager
 * 상품 설명(Description) 영역의 이미지를 관리하고 자동으로 상품 이미지를 삽입합니다.
 * 
 * 기능:
 * 1. 외부 이미지 링크 자동 제거
 * 2. 깨진 이미지 감지 및 제거
 * 3. 현재 상품 이미지 자동 삽입 (콜라주, 그리드, 배너 등)
 * 4. 빈 Description 영역에 상품 이미지 자동 채우기
 * 5. 다양한 레이아웃 디자인 지원
 * 6. 이미지 개수/크기/위치 커스터마이징
 */

// 라이트박스 닫기 버튼은 테마 공통 컴포넌트(theme-lightbox__toggle)를 쓴다.
// JS 는 Liquid 이 아니라 {% inline_asset_content %} 를 못 쓰므로 assets/icon-close.svg 를
// 그대로 옮겨 둔다. 아이콘을 교체할 일이 생기면 그 파일과 여기를 함께 고칠 것.
// (settings.system_icon_thickness 의 thin 변형은 JS 경로에선 적용되지 않는다.)
const CLOSE_ICON =
  '<svg class="theme-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">' +
  '<path d="M4.70711 3.29289C4.31658 2.90237 3.68342 2.90237 3.29289 3.29289C2.90237 3.68342 2.90237 4.31658 3.29289 4.70711L8.58579 10L3.29289 15.2929C2.90237 15.6834 2.90237 16.3166 3.29289 16.7071C3.68342 17.0976 4.31658 17.0976 4.70711 16.7071L10 11.4142L15.2929 16.7071C15.6834 17.0976 16.3166 17.0976 16.7071 16.7071C17.0976 16.3166 17.0976 15.6834 16.7071 15.2929L11.4142 10L16.7071 4.70711C17.0976 4.31658 17.0976 3.68342 16.7071 3.29289C16.3166 2.90237 15.6834 2.90237 15.2929 3.29289L10 8.58579L4.70711 3.29289Z" fill="black"/>' +
  '</svg>';
// 로케일 문자열은 theme.liquid 의 window.accessibilityStrings 로 넘어온다(없으면 영어 폴백).
const CLOSE_LABEL = (window.accessibilityStrings && window.accessibilityStrings.close) || 'Close';

// 중복 로드 방지: 이미 클래스가 정의되어 있으면 스크립트 실행 중단
if (typeof window.ProductDescriptionImageManager !== 'undefined') {
  console.log('[PDIM] Already loaded, skipping duplicate initialization');
} else {

class ProductDescriptionImageManager {
  // 클래스 상수 (매번 객체 생성 방지)
  static SIZE_MAP = {
    small: { size: '300x300', width: 300 },
    medium: { size: '600x600', width: 600 },
    large: { size: '1000x1000', width: 1000 }
  };
  
  static RATIO_MAP = {
    auto: 1,
    square: 1,
    portrait: 4 / 3,
    landscape: 3 / 4
  };
  
  static ASPECT_RATIO_CSS = {
    square: '1 / 1',
    portrait: '3 / 4',
    landscape: '4 / 3'
  };
  
  static MOSAIC_PATTERNS = [
    'pdim-mosaic-large',
    'pdim-mosaic-medium',
    'pdim-mosaic-medium',
    'pdim-mosaic-small'
  ];

  // 기본 설정 상수 (중복 제거)
  static DEFAULT_SETTINGS = {
    // Description 영역 셀렉터 (기본값)
    // ⚠️ 상품 설명을 출력하는 탭 하나만 가리켜야 한다. 탭 래퍼(.horizontal-tabs__wrapper)를
    // 대상으로 삼으면 Shipping & Returns / Care Guide 등 다른 탭의 내용까지 한 덩어리로
    // 취급돼, insertPosition:'middle' 이 엉뚱한 탭 한가운데에 이미지를 넣는다.
    // 표식은 sections/main-product-horizontal-tabs.liquid 가 show_description 탭에만 찍는다.
    selectors: [
      '[data-pdim-description]'
    ],

    // 표식이 없는 구버전 마크업용 폴백(정확한 대상을 못 찾았을 때만 사용)
    fallbackSelectors: [
      '.horizontal-tabs__wrapper'
    ],
    
    // ✅ 개선: 정상 이미지 보호 (Shopify CDN 이미지는 절대 제거)
    detectBrokenImages: true,         // 깨진 이미지만 감지 & 제거
    removeExternalImages: true,       // 외부 이미지 자동 제거
    whitelist: ['cdn.shopify.com', 'shopify.com', 'shopifyfile.oss-accelerate.aliyuncs.com'], // 화이트리스트 도메인 (제거 대상 제외) - Shopify CDN 기본 등록
    
    // 상품 이미지 삽입 (기본 활성화)
    // ✅ 개선: 기존 정상 이미지가 있으면 새 이미지 삽입 안 함
    insertProductImages: true,        // 상품 이미지 자동 삽입
    imageCount: 5,                    // 삽입할 이미지 개수
    imageSize: 'medium',              // 중간 크기 (600x600)
    layout: 'collage',                // 콜라주 레이아웃 (추천)
    insertPosition: 'bottom',         // 하단에 삽입
    fillEmptyDescription: true,       // 빈 Description 자동 채우기 (기존 이미지 있으면 무시)
    
    // 레이아웃 세부 설정
    gridColumns: 2,
    collageStyle: 'mixed',
    verticalGap: 10,
    horizontalGap: 10,
    imageRatio: 'auto',
    
    // UI
    showCaption: false,
    enableLightbox: true,
    animateOnLoad: true,
    
    // 디버그
    debug: false,  // 기본값, 나중에 덮어씌움
    showRemovedCount: false  // 기본값, 나중에 덮어씌움
  };

  constructor(options = {}) {
    // 어떤 진입점으로 들어와도(snake_case 섹션 설정 / camelCase 옵션) 같은 이름으로 정규화한다.
    this.options = {
      ...ProductDescriptionImageManager.DEFAULT_SETTINGS,
      ...ProductDescriptionImageManager.normalizeSettings(options)
    };

    this.removedCount = 0;
    this.insertedCount = 0;
    this.observer = null;
    this.productImages = [];
    this.productData = null;
    this.isTouchDevice = false;
    this.isMobile = false;

    // 🚀 성능 최적화: URL 판단 결과 캐싱
    this.urlCache = new Map();
    this.domainCache = new Map();

    // 📊 처리 상태 추적
    this.processingState = {
      isProcessing: false,
      processedElements: 0,
      totalElements: 0,
      startTime: null
    };

    // 모바일 디바이스 감지
    this.detectDeviceType();

    // 전역 옵션 저장 (재시작 시 참조용)
    if (!window.pdimLastOptions) {
      window.pdimLastOptions = {};
    }
    window.pdimLastOptions = { ...this.options };

    this.init();
  }

  /**
   * 디바이스 타입 감지 (크로스 브라우저 및 모바일 최적화 강화)
   */
  detectDeviceType() {
    // 브라우저 호환성 체크
    this.browserSupport = this.checkBrowserSupport();

    // 터치 디바이스 감지 (크로스 브라우저)
    this.isTouchDevice = this.checkTouchSupport();

    // 모바일 디바이스 감지 (화면 너비 + 터치 + userAgent)
    this.isMobile = this.checkMobileDevice();

    // 하드웨어 성능 감지
    this.deviceCapabilities = this.detectDeviceCapabilities();

    // 모바일 최적화가 활성화된 경우 설정 조정
    if (this.options.mobileOptimization && this.isMobile) {
      // 모바일에서 이미지 개수 축소
      if (this.options.reduceImageCountOnMobile) {
        this.options.imageCount = Math.min(this.options.imageCount, 3);
      }

      // 라이트박스는 모바일에서도 켠 채로 둔다. 예전엔 '성능 최적화'라며 터치 + 768px 이하에서
      // enableLightbox 를 꺼버렸는데, 정작 모바일이 확대해서 볼 필요가 가장 큰 환경이라
      // 머천트가 켜 둔 설정이 조용히 무시되는 것으로만 체감됐다(팝업이 아예 안 열림).
      // 라이트박스는 클릭 시점에 DOM 을 만들므로 켜 두는 것 자체의 비용은 없다.

      // 저성능 디바이스에서 애니메이션 비활성화
      if (!this.deviceCapabilities.highPerformance) {
        this.options.animateOnLoad = false;
      }

      this.log(`📱 모바일 최적화 적용: 터치=${this.isTouchDevice}, 모바일=${this.isMobile}, 고성능=${this.deviceCapabilities.highPerformance}, 이미지개수=${this.options.imageCount}`);
    }
  }

  /**
   * 브라우저 지원 체크
   */
  checkBrowserSupport() {
    const support = {
      mutationObserver: !!window.MutationObserver,
      fetch: !!window.fetch,
      promise: !!window.Promise,
      url: !!window.URL,
      intersectionObserver: !!window.IntersectionObserver,
      webgl: this.checkWebGLSupport(),
      serviceWorker: !!navigator.serviceWorker
    };

    // 필수 기능 지원도 계산
    support.coreFeatures = support.mutationObserver && support.fetch && support.promise;

    this.log('🌐 브라우저 지원:', support);
    return support;
  }

  /**
   * 터치 지원 체크 (크로스 브라우저)
   */
  checkTouchSupport() {
    return (
      ('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0) ||
      (navigator.msMaxTouchPoints > 0) ||
      // iOS Safari 터치 감지
      (window.DocumentTouch && document instanceof window.DocumentTouch) ||
      // 안드로이드 터치 감지
      (navigator.userAgent.includes('Mobile') && navigator.userAgent.includes('Android'))
    );
  }

  /**
   * 모바일 디바이스 체크 (종합적)
   */
  checkMobileDevice() {
    const ua = navigator.userAgent;
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // 화면 크기 기반
    const isSmallScreen = Math.min(screenWidth, screenHeight) <= 768;

    // 터치 기반
    const hasTouch = this.isTouchDevice;

    // User Agent 기반
    const mobileKeywords = ['Mobile', 'Android', 'iPhone', 'iPad', 'Windows Phone'];
    const isMobileUA = mobileKeywords.some(keyword => ua.includes(keyword));

    // 종합 판단 (OR 조건)
    return isSmallScreen || hasTouch || isMobileUA;
  }

  /**
   * 디바이스 성능 감지
   */
  detectDeviceCapabilities() {
    const capabilities = {
      highPerformance: false,
      supportsWebGL: this.checkWebGLSupport(),
      memoryEstimate: this.estimateMemory(),
      connectionSpeed: this.estimateConnectionSpeed()
    };

    // 고성능 판단 기준
    capabilities.highPerformance =
      capabilities.supportsWebGL &&
      capabilities.memoryEstimate > 100 && // 100MB 이상
      capabilities.connectionSpeed > 1; // 1Mbps 이상

    return capabilities;
  }

  /**
   * WebGL 지원 체크
   */
  checkWebGLSupport() {
    try {
      const canvas = document.createElement('canvas');
      return !!(window.WebGLRenderingContext &&
        canvas.getContext('webgl'));
    } catch {
      return false;
    }
  }

  /**
   * 메모리 용량 추정
   */
  estimateMemory() {
    // performance.memory가 있는 경우
    if (performance.memory) {
      return Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024);
    }

    // 추정치 (화면 크기와 디바이스 타입 기반)
    const screenArea = window.innerWidth * window.innerHeight;
    let estimate = 50; // 기본 50MB

    if (this.isMobile) {
      estimate = screenArea > 300000 ? 100 : 50; // 큰 화면 모바일은 100MB
    } else {
      estimate = screenArea > 1000000 ? 200 : 100; // 큰 화면 데스크톱은 200MB
    }

    return estimate;
  }

  /**
   * 연결 속도 추정
   */
  estimateConnectionSpeed() {
    // Navigator.connection이 있는 경우 (Chrome 등)
    if (navigator.connection && navigator.connection.effectiveType) {
      const type = navigator.connection.effectiveType;
      const speedMap = {
        '4g': 10,   // 10 Mbps
        '3g': 1,    // 1 Mbps
        '2g': 0.1,  // 100 Kbps
        'slow-2g': 0.05 // 50 Kbps
      };
      return speedMap[type] || 1;
    }

    // 추정치 (모바일은 느린 것으로 가정)
    return this.isMobile ? 1 : 10;
  }

  /**
   * 초기화
   */
  async init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.start());
    } else {
      await this.start();
    }
  }

  /**
   * 시작 (병렬 처리 및 진행률 표시 개선)
   */
  async start() {
    // 처리 상태 초기화
    this.processingState.isProcessing = true;
    this.processingState.startTime = Date.now();
    this.processingState.processedElements = 0;
    this.processingState.totalElements = 0;

    try {
      // 상품 데이터 수집
      await this.collectProductData();

      // Description 영역 찾기
      const descriptionElements = this.findDescriptionElements();

      if (descriptionElements.length === 0) {
        return;
      }

      this.processingState.totalElements = descriptionElements.length;

      // 🚀 병렬 처리로 성능 개선 (최대 3개 동시 처리)
      const concurrencyLimit = Math.min(3, descriptionElements.length);
      const results = [];

      for (let i = 0; i < descriptionElements.length; i += concurrencyLimit) {
        const batch = descriptionElements.slice(i, i + concurrencyLimit);
        const batchPromises = batch.map(async (element, batchIndex) => {
          const globalIndex = i + batchIndex;
          try {
            const result = await this.processElementImages(element);
            this.processingState.processedElements++;

            // 진행률 로깅 (디버그 모드)
            if (this.options.debug) {
              const progress = Math.round((this.processingState.processedElements / this.processingState.totalElements) * 100);
              this.log(`📊 진행률: ${progress}% (${this.processingState.processedElements}/${this.processingState.totalElements})`);
            }

            return { element, result, success: true };
          } catch (error) {
            this.log('요소 처리 오류:', error);
            return { element, result: null, success: false, error };
          }
        });

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
      }

      // 결과 처리
      for (const { element, result, success } of results) {
        if (!success || !result) continue;

        if (result.hasExistingValidImages) {
          this.log('✅ 기존 정상 이미지 감지 - 새 이미지 삽입 스킵');
        } else {
          this.log('❌ 기존 이미지 없음 - 새 이미지 삽입 진행');
          if (this.options.insertProductImages && this.productImages.length > 0) {
            await this.insertProductImagesToDescription(element);
          }
        }

        // 동적 변경 감지
        this.observeChanges(element);
      }

      // 처리 완료 로깅
      const duration = Date.now() - this.processingState.startTime;
      this.log(`✅ 모든 요소 처리 완료 (${duration}ms, ${this.processingState.processedElements}개 요소)`);

    } finally {
      this.processingState.isProcessing = false;
    }

    // 알림 표시 (설정된 경우에만)
    if (this.options.showRemovedCount && (this.removedCount > 0 || this.insertedCount > 0)) {
      this.showNotification(
        `${this.removedCount}개 제거, ${this.insertedCount}개 삽입 완료 | 기존 정상 이미지 보호됨`
      );
    }
  }
  
  /**
   * 상품 데이터 수집 (에러 복구 및 타임아웃 개선)
   */
  async collectProductData() {
    const maxRetries = 3;
    const timeout = 10000; // 10초 타임아웃

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // 타임아웃 적용
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        // 1. 페이지에서 상품 핸들 찾기
        const productHandle = this.getProductHandle();
        if (!productHandle) {
          this.log('상품 핸들을 찾을 수 없습니다');
          return;
        }

        // 2. Shopify Product JSON 가져오기 (타임아웃 적용)
        const response = await fetch(`/products/${productHandle}.js`, {
          signal: controller.signal,
          headers: {
            'Cache-Control': 'no-cache' // 캐시 방지로 최신 데이터 확보
          }
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        this.productData = await response.json();

        // 3. 이미지 데이터 추출 (에러 처리 강화)
        if (this.productData.media) {
          // media 객체가 있는 경우 (최신 Shopify)
          this.productImages = this.productData.media
            .filter(m => m && m.media_type === 'image')
            .map(m => ({
              src: m.src || '',
              alt: m.alt || this.productData.title || '',
              width: m.width || 0,
              height: m.height || 0,
              id: m.id || 0
            }))
            .filter(img => img.src); // 빈 src 필터링
        } else if (this.productData.images) {
          // images 배열만 있는 경우 (레거시)
          this.productImages = this.productData.images
            .filter(src => src && typeof src === 'string')
            .map((src, index) => ({
              src: src,
              alt: this.productData.title || '',
              id: index
            }));
        }

        // 유효한 이미지만 필터링
        this.productImages = this.productImages.filter(img =>
          img.src && this.isValidImageUrl(img.src)
        );

        this.log(`${this.productImages.length}개 유효 이미지 로드 완료 (시도 ${attempt}/${maxRetries})`);
        return;

      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const errorMsg = `상품 데이터 수집 실패 (시도 ${attempt}/${maxRetries}): ${error.message}`;

        if (isLastAttempt) {
          this.log('❌ ' + errorMsg + ' - 기본 이미지 사용');
          // 실패 시 빈 배열로 설정 (크래시 방지)
          this.productImages = [];
          return;
        } else {
          this.log('⚠️ ' + errorMsg + ' - 재시도 중...');
          // 지수 백오프 (1초, 2초, 4초...)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt - 1) * 1000));
        }
      }
    }
  }

  /**
   * 이미지 URL 유효성 검증 헬퍼
   */
  isValidImageUrl(url) {
    if (!url || typeof url !== 'string') return false;

    try {
      const urlObj = new URL(url.startsWith('//') ? 'https:' + url : url);

      // HTTP/HTTPS 프로토콜 확인
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return false;
      }

      // 기본적인 이미지 확장자 확인 (선택적)
      const pathname = urlObj.pathname.toLowerCase();
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
      const hasImageExtension = imageExtensions.some(ext => pathname.endsWith(ext));

      return hasImageExtension || !pathname.includes('.'); // 확장자가 없거나 이미지 확장자인 경우
    } catch {
      return false;
    }
  }
  
  /**
   * 상품 핸들 가져오기
   */
  getProductHandle() {
    // 1. data-product-handle 속성에서 찾기
    const productSection = document.querySelector('[data-product-handle]');
    if (productSection) {
      return productSection.getAttribute('data-product-handle');
    }
    
    // 2. URL에서 추출
    const urlMatch = window.location.pathname.match(/\/products\/([^/?]+)/);
    if (urlMatch) {
      return urlMatch[1];
    }
    
    // 3. meta 태그에서 찾기
    const metaProduct = document.querySelector('meta[property="og:url"]');
    if (metaProduct) {
      const ogUrlMatch = metaProduct.content.match(/\/products\/([^/?]+)/);
      if (ogUrlMatch) {
        return ogUrlMatch[1];
      }
    }
    
    return null;
  }

  /**
   * Description 영역 찾기
   */
  findDescriptionElements() {
    const collect = (selectorList) => {
      const elements = [];
      (selectorList || []).forEach(selector => {
        try {
          document.querySelectorAll(selector).forEach(el => {
            if (!elements.includes(el)) elements.push(el);
          });
        } catch (e) {
          this.log(`셀렉터 오류: ${selector}`, e);
        }
      });
      return elements;
    };

    const elements = collect(this.options.selectors);
    if (elements.length > 0) return elements;

    // 정확한 대상을 못 찾은 경우에만 폴백. 폴백은 탭 래퍼처럼 범위가 넓어서
    // 다른 탭까지 건드릴 수 있으므로 로그를 남긴다.
    const fallback = collect(this.options.fallbackSelectors);
    if (fallback.length > 0) {
      this.log('⚠️ [data-pdim-description] 를 찾지 못해 폴백 셀렉터 사용', this.options.fallbackSelectors);
    }
    return fallback;
  }

  /**
   * 통합 이미지 처리 (효율성 및 일관성 개선)
   */
  async processElementImages(element) {
    // 🔍 1회 DOM 탐색으로 모든 이미지 수집
    const images = Array.from(element.querySelectorAll('img'));

    if (images.length === 0) {
      this.log('❌ 처리할 이미지 없음');
      return { hasExistingValidImages: false, processedCount: 0 };
    }

    // 📊 이미지 상태 분석 (한 번에 모든 판단 수행)
    const imageAnalysis = this.analyzeImages(images);

    // 🧹 이미지 정리 (안전 모드 적용)
    const processedCount = await this.processImagesByCategory(images, imageAnalysis);

    // 📋 결과 반환
    return {
      hasExistingValidImages: imageAnalysis.hasValidImages,
      processedCount: processedCount
    };
  }

  /**
   * 이미지 상태 통합 분석
   */
  analyzeImages(images) {
    const analysis = {
      hasValidImages: false,
      shopifyCDN: [],
      localDomain: [],
      whitelisted: [],
      external: [],
      broken: []
    };

    const currentDomain = window.location.hostname;

    images.forEach(img => {
      const src = img.src || img.getAttribute('src') || '';

      // Shopify CDN 이미지
      if (this.isShopifyCDNImage(img)) {
        analysis.shopifyCDN.push(img);
        analysis.hasValidImages = true;
        return;
      }

      // 로컬 도메인 이미지 (정확한 매칭으로 개선)
      if (this.isDomainMatch(src, currentDomain)) {
        analysis.localDomain.push(img);
        analysis.hasValidImages = true;
        return;
      }

      // 화이트리스트 도메인 (정확한 매칭으로 개선)
      if (this.isWhitelistedDomain(src)) {
        analysis.whitelisted.push(img);
        return;
      }

      // 외부 이미지 (제거 대상)
      analysis.external.push(img);
    });

    this.log(`📊 이미지 분석 완료:`, {
      total: images.length,
      valid: analysis.hasValidImages,
      shopify: analysis.shopifyCDN.length,
      local: analysis.localDomain.length,
      whitelisted: analysis.whitelisted.length,
      external: analysis.external.length
    });

    return analysis;
  }

  /**
   * URL에서 안전하게 호스트네임 추출 헬퍼
   */
  extractHostname(url) {
    if (!url) return null;

    try {
      // 프로토콜 없는 URL 처리 (//example.com/image.jpg)
      if (url.startsWith('//')) {
        url = 'https:' + url;
      }

      // 상대 경로 처리
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        // 상대 경로인 경우 현재 도메인으로 간주
        return window.location.hostname;
      }

      const urlObj = new URL(url);
      return urlObj.hostname.toLowerCase();
    } catch (error) {
      // URL 파싱 실패 시 null 반환
      this.log('URL 파싱 실패:', url, error.message);
      return null;
    }
  }

  /**
   * 도메인 매칭 헬퍼 (캐시 적용으로 성능 최적화)
   */
  isDomainMatch(url, targetDomain) {
    if (!url || !targetDomain) return false;

    // 캐시 키 생성
    const cacheKey = `${url}::${targetDomain}`;

    // 캐시 확인
    if (this.domainCache.has(cacheKey)) {
      return this.domainCache.get(cacheKey);
    }

    const hostname = this.extractHostname(url);
    if (!hostname) {
      this.domainCache.set(cacheKey, false);
      return false;
    }

    const normalizedTarget = targetDomain.toLowerCase().trim();

    // 정확한 매칭 + 서브도메인 지원
    const result = hostname === normalizedTarget || hostname.endsWith('.' + normalizedTarget);

    // 결과 캐싱 (메모리 제한 고려)
    if (this.domainCache.size < 1000) { // 최대 1000개 캐시
      this.domainCache.set(cacheKey, result);
    }

    return result;
  }

  /**
   * 화이트리스트 도메인 확인 헬퍼 (개선)
   */
  isWhitelistedDomain(src) {
    if (!src || !this.options.whitelist.length) return false;

    return this.options.whitelist.some(domain => this.isDomainMatch(src, domain));
  }

  /**
   * 카테고리별 이미지 처리
   */
  async processImagesByCategory(images, analysis) {
    let processedCount = 0;

    // 외부 이미지 제거 (설정에 따라)
    if (this.options.removeExternalImages) {
      for (const img of analysis.external) {
        if (this.shouldRemoveImage(img)) {
          this.removeImage(img);
          processedCount++;
        }
      }
    }

    // 깨진 이미지 검증 (일반 모드에서만)
    if (this.options.detectBrokenImages) {
      const allImagesToCheck = [...analysis.shopifyCDN, ...analysis.localDomain, ...analysis.whitelisted];
      for (const img of allImagesToCheck) {
        this.checkBrokenImage(img);
      }
    }

    return processedCount;
  }
  
  /**
   * ✅ 개선: 상품 설명에 정상 Shopify 이미지 존재 여부 판단 (더 이상 사용되지 않음 - 통합 메소드로 대체)
   * @deprecated Use processElementImages() instead
   */
  hasExistingProductImages(element) {
    const images = element.querySelectorAll('img');
    
    if (images.length === 0) {
      this.log('❌ 기존 이미지 없음');
      return false;
    }
    
    // Shopify CDN 이미지 확인
    for (const img of images) {
      if (this.isShopifyCDNImage(img)) {
        this.log(`✅ Shopify CDN 정상 이미지 감지 (${img.src.substring(0, 50)}...)`);
        return true;
      }
    }
    
    // 현재 도메인 이미지 확인
    const currentDomain = window.location.hostname;
    for (const img of images) {
      const src = img.src || '';
      if (src && src.includes(currentDomain)) {
        this.log(`✅ 로컬 정상 이미지 감지 (${src.substring(0, 50)}...)`);
        return true;
      }
    }
    
    this.log('⚠️ 외부 이미지만 존재 - 제거 후 새 이미지 삽입 진행');
    return false;
  }

  /**
   * Description에 상품 이미지 삽입
   */
  async insertProductImagesToDescription(element) {
    // 빈 Description 체크
    const hasContent = this.hasTextContent(element);

    if (!hasContent && !this.options.fillEmptyDescription) {
      return;
    }

    // 🔄 중복 체크 제거 (이미 processElementImages에서 확인됨)
    // 이미 삽입된 상품 이미지가 있는지 체크
    if (element.querySelector('.pdim-product-images')) {
      return;
    }

    // 삽입 위치 결정
    const insertPoint = this.determineInsertPosition(element);

    // 이미지 컨테이너 생성
    const imageContainer = this.createImageContainer();

    // 삽입
    this.insertAtPosition(element, imageContainer, insertPoint);

    this.insertedCount++;
  }
  
  /**
   * 텍스트 콘텐츠 존재 여부 확인
   */
  hasTextContent(element) {
    const text = element.textContent.trim();
    // 이미지만 있고 텍스트가 거의 없는 경우 빈 것으로 간주
    return text.length > 20;
  }
  
  /**
   * 삽입 위치 결정
   */
  determineInsertPosition(element) {
    const position = this.options.insertPosition;
    
    if (position === 'top') {
      return { type: 'prepend', target: element };
    }
    
    if (position === 'bottom') {
      return { type: 'append', target: element };
    }
    
    if (position === 'middle') {
      // 중간 위치 찾기 (단락 사이).
      // ⚠️ 직계 자식만 본다. 하위 전체를 훑으면 중첩된 노드나 이웃 탭의 단락까지 후보에
      // 들어가, 삽입 지점이 설명 바깥으로 튀어나간다.
      const blocks = Array.from(element.children).filter(el =>
        /^(P|H2|H3|H4|DIV|UL|OL|SECTION)$/.test(el.tagName)
      );
      if (blocks.length >= 2) {
        // 중앙 블록 '앞'에 넣는다. 뒤에 넣으면 2블록 설명에서 마지막 블록 뒤 =
        // 하단 삽입으로 퇴화해 '단락 사이'가 성립하지 않는다(2블록 → 두 단락 사이,
        // 4블록 → 2번째 뒤가 정확한 중앙).
        const middleIndex = Math.floor(blocks.length / 2);
        return { type: 'before', target: blocks[middleIndex] };
      }
      return { type: 'append', target: element };
    }
    
    if (position === 'auto') {
      // 자동: 콘텐츠가 있으면 하단, 없으면 상단
      const hasContent = this.hasTextContent(element);
      return hasContent 
        ? { type: 'append', target: element }
        : { type: 'prepend', target: element };
    }
    
    return { type: 'append', target: element };
  }
  
  /**
   * 위치에 삽입
   */
  insertAtPosition(element, container, insertPoint) {
    switch (insertPoint.type) {
      case 'prepend':
        insertPoint.target.insertBefore(container, insertPoint.target.firstChild);
        break;
      case 'append':
        insertPoint.target.appendChild(container);
        break;
      case 'after':
        insertPoint.target.parentNode.insertBefore(container, insertPoint.target.nextSibling);
        break;
      case 'before':
        insertPoint.target.parentNode.insertBefore(container, insertPoint.target);
        break;
    }
  }

  /**
   * ✅ 개선: Shopify CDN 이미지 판단
   */
  isShopifyCDNImage(img) {
    const src = img.src || img.getAttribute('src') || img.getAttribute('data-src') || '';
    if (!src) return false;
    
    const normalizedSrc = src.trim().toLowerCase();
    return normalizedSrc.includes('cdn.shopify.com') || normalizedSrc.includes('shopify.com');
  }
  
  /**
   * ✅ 개선: 외부 이미지 판단
   */
  isExternalImage(img) {
    const src = img.src || img.getAttribute('src') || img.getAttribute('data-src') || '';
    if (!src) return false;
    
    const normalizedSrc = src.trim().toLowerCase();
    
    // Shopify CDN 이미지는 외부가 아님
    if (normalizedSrc.includes('cdn.shopify.com') || normalizedSrc.includes('shopify.com')) {
      return false;
    }
    
    // 현재 도메인 이미지는 외부가 아님
    const currentDomain = window.location.hostname;
    if (normalizedSrc.includes(currentDomain)) {
      return false;
    }
    
    // http/https로 시작하고 외부 도메인이면 외부 이미지
    return /^https?:\/\//i.test(normalizedSrc);
  }

  /**
   * 이미지 제거 여부 판단
   */
  shouldRemoveImage(img) {
    // src 또는 data-src 속성 확인 (lazy loading 대응)
    const src = img.src || img.getAttribute('src') || img.getAttribute('data-src') || '';
    
    // src가 없는 경우
    if (!src || src === '') {
      return false;
    }

    // URL 정규화 (공백 제거)
    const normalizedSrc = src.trim().replace(/\s+/g, '');

    // 외부 URL 체크 (http:// 또는 https://로 시작)
    if (!/^https?:\/\//i.test(normalizedSrc)) {
      return false;
    }

    // 현재 도메인인 경우 제외 (정확한 매칭으로 개선)
    const currentDomain = window.location.hostname;
    if (this.isDomainMatch(normalizedSrc, currentDomain)) {
      return false;
    }

    // ✅ 개선: Shopify CDN 이미지는 절대 제거하지 않음 (우선순위 높음)
    if (this.isDomainMatch(normalizedSrc, 'cdn.shopify.com') ||
        this.isDomainMatch(normalizedSrc, 'shopify.com')) {
      this.log('🛡️ Shopify CDN 이미지는 제거 불가:', normalizedSrc);
      return false;
    }

    // 화이트리스트 체크 (정확한 매칭으로 개선)
    if (this.options.whitelist.length > 0) {
      const isWhitelisted = this.options.whitelist.some(domain =>
        this.isDomainMatch(normalizedSrc, domain)
      );

      if (isWhitelisted) {
        return false;
      }
    }

    // 외부 이미지로 판단
    return true;
  }

  /**
   * 이미지 제거
   */
  removeImage(img) {
    // 대체 콘텐츠가 있는 경우
    if (this.options.replacement) {
      const replacement = document.createElement('div');
      replacement.className = 'removed-image-placeholder';
      replacement.innerHTML = this.options.replacement;
      img.parentNode.replaceChild(replacement, img);
    } else {
      // 이미지 완전 제거
      img.parentNode && img.parentNode.removeChild(img);
    }

    this.removedCount++;
  }

  /**
   * 깨진 이미지 감지 (보수적 개선 버전)
   */
  checkBrokenImage(img) {
    // 이미 처리된 이미지인지 체크 (중복 처리 방지)
    if (img.dataset.brokenChecked) {
      return;
    }
    img.dataset.brokenChecked = 'true';

    // 화이트리스트 도메인 확인 (보수적 접근)
    if (this.isWhitelistedDomain(img.src)) {
      this.log('화이트리스트 도메인 이미지 - 검증 생략:', img.src);
      return;
    }

    // 이미 로드 완료된 경우 즉시 검증
    if (img.complete) {
      this.validateImageIntegrity(img);
      return;
    }

    // 타임아웃 설정 (보수적으로 15초로 증가)
    const timeoutId = setTimeout(() => {
      this.log('이미지 로드 타임아웃 (보수적):', img.src);
      // 타임아웃 시에도 한 번 더 검증 시도
      this.attemptRecoveryValidation(img);
    }, 15000);

    // 로드 성공 이벤트
    const onLoad = () => {
      clearTimeout(timeoutId);
      this.validateImageIntegrity(img);
    };

    // 로드 실패 이벤트
    const onError = () => {
      clearTimeout(timeoutId);
      this.log('이미지 로드 실패:', img.src);
      // 실패 시에도 복구 검증 시도
      this.attemptRecoveryValidation(img);
    };

    // 이벤트 리스너 추가 (한 번만 실행)
    img.addEventListener('load', onLoad, { once: true });
    img.addEventListener('error', onError, { once: true });
  }

  /**
   * 복구 검증 시도 (보수적 접근)
   */
  attemptRecoveryValidation(img) {
    // 짧은 지연 후 다시 검증 (네트워크 지연 고려)
    setTimeout(() => {
      if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
        this.log('복구 검증 성공:', img.src);
        return;
      }
      
      // 여전히 실패하면 제거
      this.log('복구 검증 실패 - 이미지 제거:', img.src);
      this.removeImage(img);
    }, 2000);
  }

  /**
   * 이미지 무결성 검증 (보수적 개선)
   */
  validateImageIntegrity(img) {
    // 기본 검증: naturalWidth와 naturalHeight가 0이면 깨진 이미지로 판단
    if (img.naturalWidth === 0 || img.naturalHeight === 0) {
      this.log('깨진 이미지 감지 (무결성 실패):', img.src);
      this.removeImage(img);
      return;
    }

    // 보수적 검증 1: 파일 크기가 너무 작은 경우 (2x2 픽셀 미만)
    if (img.naturalWidth < 2 && img.naturalHeight < 2) {
      this.log('의심스러운 매우 작은 이미지:', img.src, `${img.naturalWidth}x${img.naturalHeight}`);
      this.removeImage(img);
      return;
    }

    // 보수적 검증 2: 비정상적인 aspect ratio (너무 길거나 넓은 경우)
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    if (aspectRatio > 100 || aspectRatio < 0.01) {
      this.log('비정상적인 aspect ratio 감지:', img.src, `ratio: ${aspectRatio}`);
      this.removeImage(img);
      return;
    }

    // 보수적 검증 3: 최소 크기 요구사항 (너무 작은 이미지는 제거)
    const minDimension = 10; // 최소 10px
    if (img.naturalWidth < minDimension || img.naturalHeight < minDimension) {
      this.log('최소 크기 미달 이미지:', img.src, `${img.naturalWidth}x${img.naturalHeight}`);
      this.removeImage(img);
      return;
    }

    this.log('이미지 검증 통과 (보수적):', img.src);
  }

  /**
   * 변경 사항 감지 (동적 콘텐츠)
   */
  observeChanges(element) {
    if (!window.MutationObserver) {
      return;
    }

    this.observer = new MutationObserver((mutations) => {
      let shouldProcess = false;

      mutations.forEach(mutation => {
        // 새로운 노드가 추가된 경우
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeName === 'IMG' || 
                (node.querySelectorAll && node.querySelectorAll('img').length > 0)) {
              shouldProcess = true;
            }
          });
        }
      });

      if (shouldProcess) {
        this.log('콘텐츠 변경 감지 - 이미지 재검사');
        this.processElementImages(element);
      }
    });

    this.observer.observe(element, {
      childList: true,
      subtree: true
    });
  }
  
  /**
   * 이미지 컨테이너 생성
   */
  createImageContainer() {
    const container = document.createElement('div');
    container.className = `pdim-product-images pdim-layout-${this.options.layout}`;
    
    // 디버그 모드일 때만 데이터 속성 추가
    if (this.options.debug) {
      container.dataset.layout = this.options.layout;
      container.dataset.imageSize = this.options.imageSize;
    }
    
    // 애니메이션 클래스 추가
    if (this.options.animateOnLoad) {
      container.classList.add('pdim-animate');
    }
    
    // 이미지 개수 제한
    const imageCount = Math.min(this.options.imageCount, this.productImages.length);
    const imagesToUse = this.productImages.slice(0, imageCount);
    
    // 레이아웃별 렌더링
    switch (this.options.layout) {
      case 'single':
        this.renderSingleLayout(container, imagesToUse);
        break;
      case 'grid':
        this.renderGridLayout(container, imagesToUse);
        break;
      case 'collage':
        this.renderCollageLayout(container, imagesToUse);
        break;
      case 'banner':
        this.renderBannerLayout(container, imagesToUse);
        break;
      case 'masonry':
        this.renderMasonryLayout(container, imagesToUse);
        break;
      default:
        this.renderGridLayout(container, imagesToUse);
    }
    
    return container;
  }
  
  /**
   * Single 레이아웃 (1개 이미지)
   */
  renderSingleLayout(container, images) {
    const { responsiveSize } = this.setCommonCSSVariables(container);
    
    images.forEach((image, index) => {
      const img = this.createImageElement(image, index, responsiveSize);
      const wrapper = document.createElement('div');
      wrapper.className = 'pdim-single-wrapper';
      wrapper.appendChild(img);
      container.appendChild(wrapper);
    });
  }
  
  /**
   * Shopify CDN 이미지 URL 변환 헬퍼
   */
  static transformShopifyImageUrl(src, size) {
    if (!src || !src.includes('cdn.shopify.com')) {
      return src;
    }
    
    const sizeConfig = ProductDescriptionImageManager.SIZE_MAP[size] || ProductDescriptionImageManager.SIZE_MAP.medium;
    return src.replace(/(\.(jpg|jpeg|png|gif|webp))/i, `_${sizeConfig.size}$1`);
  }

  /**
   * CSS 변수 배치 설정 헬퍼 (성능 최적화)
   */
  static setCSSVariablesBatch(element, variables) {
    const style = element.style;
    Object.entries(variables).forEach(([key, value]) => {
      style.setProperty(key, value);
    });
  }

  /**
   * snake_case를 camelCase로 변환 헬퍼
   */
  static convertSnakeToCamel(obj) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      result[camelKey] = value;
    }
    return result;
  }

  /**
   * 섹션 설정(schema id) → 옵션 이름 정규화.
   * ⚠️ 이름이 기계적으로 대응하지 않는 항목이 있어 camelCase 변환만으로는 설정이 조용히
   * 무시된다(예: layout_type → layoutType 이지만 코드가 읽는 건 layout). 여기 한 곳에서
   * 별칭을 잇고, 진입점(섹션 인라인 스크립트·자동실행 IIFE·updatePDIMSettings)은 모두
   * 생성자를 거치므로 어디로 들어와도 같은 결과가 된다. camelCase 입력에는 무해(멱등).
   */
  static normalizeSettings(raw) {
    if (!raw || typeof raw !== 'object') return {};

    const result = ProductDescriptionImageManager.convertSnakeToCamel(raw);

    const alias = (from, ...targets) => {
      if (raw[from] === undefined) return;
      targets.forEach(key => { result[key] = raw[from]; });
    };

    alias('layout_type', 'layout');                     // 레이아웃 스타일(single/grid/collage/…)
    alias('image_gap', 'verticalGap', 'horizontalGap'); // 간격 하나로 가로·세로 동시 지정
    alias('enable_auto_images', 'insertProductImages');
    alias('fill_empty_description', 'fillEmptyDescription');
    alias('remove_broken_images', 'detectBrokenImages');
    alias('remove_external_images', 'removeExternalImages');

    // 화이트리스트는 줄바꿈/쉼표 구분 문자열 → 배열. 비어 있으면 기본값을 살린다.
    if (typeof raw.whitelist_domains === 'string') {
      const domains = raw.whitelist_domains
        .split(/[\n,]+/)
        .map(domain => domain.trim())
        .filter(domain => domain.length > 0);
      if (domains.length > 0) result.whitelist = domains;
      else delete result.whitelistDomains;
    }

    return result;
  }

  /**
   * 공통 CSS 변수 설정 헬퍼 (배치 처리로 성능 최적화)
   */
  setCommonCSSVariables(container, includeColumns = false) {
    const responsiveSize = this.getResponsiveImageSize();
    const sizeConfig = ProductDescriptionImageManager.SIZE_MAP[responsiveSize] || ProductDescriptionImageManager.SIZE_MAP.medium;
    
    const variables = {
      '--pdim-vertical-gap': `${this.options.verticalGap}px`,
      '--pdim-horizontal-gap': `${this.options.horizontalGap}px`,
      '--pdim-image-size': `${sizeConfig.width}px`
    };
    
    if (includeColumns) {
      variables['--pdim-columns'] = this.options.gridColumns;
    }
    
    ProductDescriptionImageManager.setCSSVariablesBatch(container, variables);
    
    return { responsiveSize, sizeConfig };
  }

  /**
   * 반응형 이미지 크기 결정
   * 화면 크기에 따라 최적의 이미지 크기 선택
   */
  getResponsiveImageSize() {
    const screenWidth = window.innerWidth;
    const requestedSize = this.options.imageSize;
    
    // 모바일 (화면 너비 768px 이하)
    if (screenWidth <= 768) {
      // large 요청 시도 medium으로 축소
      if (requestedSize === 'large') return 'medium';
      // medium 요청 시 small로 축소
      if (requestedSize === 'medium') return 'small';
      return requestedSize;
    }
    
    // 태블릿 (화면 너비 1024px 이하)
    if (screenWidth <= 1024) {
      // large 요청 시 medium으로 축소
      if (requestedSize === 'large') return 'medium';
      return requestedSize;
    }
    
    // 데스크톱 - 요청한 크기 그대로 사용
    return requestedSize;
  }

  /**
   * 이미지 비율 배수(높이/너비).
   * ⚠️ CSS 의 --pdim-aspect-ratio 는 배너에서 `calc(1 / var(...))` 로 쓰이므로 이 값은 h/w 형태다.
   * 그리드처럼 CSS aspect-ratio(w/h) 자리에 그대로 넣는 곳에서는 역수를 취해 넣어야 한다.
   */
  getAspectRatioMultiplier() {
    const map = ProductDescriptionImageManager.RATIO_MAP;
    return map[this.options.imageRatio] || map.auto;
  }

  /**
   * Grid 레이아웃 (균등 그리드)
   */
  renderGridLayout(container, images) {
    const { responsiveSize } = this.setCommonCSSVariables(container, true);
    // aspect-ratio(w/h) 자리이므로 h/w 배수의 역수를 넣는다(portrait → 3/4, landscape → 4/3).
    const aspectRatio = 1 / this.getAspectRatioMultiplier();
    container.style.setProperty('--pdim-aspect-ratio', aspectRatio);
    
    images.forEach((image, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'pdim-grid-item';
      wrapper.appendChild(this.createImageElement(image, index, responsiveSize));
      container.appendChild(wrapper);
    });
  }
  
  /**
   * Collage 레이아웃 (비대칭 배치)
   */
  renderCollageLayout(container, images) {
    const { responsiveSize } = this.setCommonCSSVariables(container);
    container.dataset.collagePattern = this.options.collageStyle;
    
    const style = this.options.collageStyle;
    const count = images.length;
    
    // Mixed 스타일일 때, 첫 번째 이미지가 극단적인 종횡비(0.8 미만 또는 1.33 초과)라면
    // Featured(2x2 대형) 슬롯에 적합한 표준 비율 이미지를 우선 배치하여 시각적 안정성 확보
    let displayImages = [...images];
    if (style === 'mixed' && images.length > 1) {
      const firstRatio = (images[0].width && images[0].height) ? (images[0].width / images[0].height) : null;
      if (firstRatio !== null && (firstRatio < 0.8 || firstRatio > 1.33)) {
        const balancedIdx = images.findIndex(img => {
          if (!img.width || !img.height) return false;
          const r = img.width / img.height;
          return r >= 0.8 && r <= 1.33;
        });
        if (balancedIdx > 0) {
          displayImages = [
            images[balancedIdx],
            ...images.filter((_, i) => i !== balancedIdx)
          ];
        }
      }
    }

    if (style === 'mixed') {
      // Mixed: 첫 이미지만 크게, 나머지는 균등한 작은 크기
      displayImages.forEach((image, index) => {
        const wrapper = document.createElement('div');
        wrapper.className = index === 0 
          ? 'pdim-collage-item pdim-collage-large' 
          : 'pdim-collage-item pdim-collage-small';
        wrapper.appendChild(this.createImageElement(image, index, responsiveSize));
        container.appendChild(wrapper);
      });
    } else if (style === 'asymmetric') {
      // Asymmetric: 다양한 크기를 비대칭으로 배치
      images.forEach((image, index) => {
        const wrapper = document.createElement('div');
        // 인덱스에 따라 다른 크기 클래스 할당
        let sizeClass;
        if (index === 0) {
          sizeClass = 'pdim-collage-large'; // 2x2
        } else if (index === 1 && count > 2) {
          sizeClass = 'pdim-collage-wide'; // 2x1
        } else {
          sizeClass = 'pdim-collage-small'; // 1x1
        }
        wrapper.className = `pdim-collage-item ${sizeClass}`;
        wrapper.appendChild(this.createImageElement(image, index, responsiveSize));
        container.appendChild(wrapper);
      });
    } else if (style === 'mosaic') {
      // 모자이크: 다양한 크기 조합
      images.forEach((image, index) => {
        const wrapper = document.createElement('div');
        const sizeClass = this.getMosaicSizeClass(index, count);
        wrapper.className = `pdim-collage-item ${sizeClass}`;
        wrapper.appendChild(this.createImageElement(image, index, responsiveSize));
        container.appendChild(wrapper);
      });
    }
  }
  
  /**
   * 모자이크 크기 클래스 결정 (정적 상수 활용)
   */
  getMosaicSizeClass(index) {
    const patterns = ProductDescriptionImageManager.MOSAIC_PATTERNS;
    return patterns[index % patterns.length];
  }
  
  /**
   * Banner 레이아웃 (가로 배너)
   */
  renderBannerLayout(container, images) {
    const { responsiveSize } = this.setCommonCSSVariables(container);
    const aspectRatio = this.options.imageRatio === 'auto' ? (9 / 16) : this.getAspectRatioMultiplier();
    container.style.setProperty('--pdim-aspect-ratio', aspectRatio);
    
    images.forEach((image, index) => {
      const item = document.createElement('div');
      item.className = 'pdim-banner-item';
      // createImageElement가 이미 wrapper를 생성하므로 중첩 제거
      item.appendChild(this.createImageElement(image, index, responsiveSize));
      container.appendChild(item);
    });
  }
  
  /**
   * Masonry 레이아웃 (벽돌 쌓기)
   */
  renderMasonryLayout(container, images) {
    const { responsiveSize } = this.setCommonCSSVariables(container, true);
    
    images.forEach((image, index) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'pdim-masonry-item';
      wrapper.appendChild(this.createImageElement(image, index, responsiveSize));
      container.appendChild(wrapper);
    });
  }
  
  /**
   * 이미지 엘리먼트 생성
   */
  createImageElement(imageData, index, overrideSize = null) {
    const wrapper = document.createElement('div');
    wrapper.className = 'pdim-image-wrapper';
    
    // 이미지 크기 결정 (반응형 크기 또는 기본 크기)
    const imageSize = overrideSize || this.options.imageSize;
    const sizeConfig = ProductDescriptionImageManager.SIZE_MAP[imageSize] || ProductDescriptionImageManager.SIZE_MAP.medium;
    
    // 모바일 최적화: 터치 디바이스에서 이미지 크기 추가 조정
    let finalSizeConfig = sizeConfig;
    if (this.options.mobileOptimization && this.isMobile) {
      // 모바일에서 이미지 크기를 더 작게 조정
      if (imageSize === 'medium') {
        finalSizeConfig = ProductDescriptionImageManager.SIZE_MAP.small;
      } else if (imageSize === 'large') {
        finalSizeConfig = ProductDescriptionImageManager.SIZE_MAP.medium;
      }
    }
    
    // Shopify CDN 이미지 URL 변환 (헬퍼 함수 사용)
    let imageSrc = ProductDescriptionImageManager.transformShopifyImageUrl(imageData.src, imageSize);
    
    // 이미지 생성
    const img = document.createElement('img');
    img.src = imageSrc || imageData.src;
    img.alt = imageData.alt || `Product image ${index + 1}`;
    img.className = 'pdim-image';
    
    // 모바일 최적화: Lazy loading 설정
    if (this.options.lazyLoadImages) {
      if (this.isMobile && index > 1) {
        // 모바일에서 첫 2개 이미지만 즉시 로드, 나머지는 lazy
        img.loading = 'lazy';
        img.setAttribute('data-src', img.src);
        img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PC9zdmc+'; // 회색 placeholder
        
        // Intersection Observer로 lazy loading 구현
        this.setupLazyLoading(img);
      } else {
        img.loading = 'lazy';
      }
    } else {
      img.loading = 'lazy';
    }
    
    // 크기 상한만 인라인으로 준다. 예전엔 width 를 고정 px 로 박아서, 그리드·콜라주처럼
    // '셀이 크기를 정하는' 레이아웃에서 300~600px 이미지가 172px 짜리 셀을 뚫고 나왔다
    // (인라인이라 .pdim-collage-item .pdim-image { width:100% } 가 이기지 못한다).
    // max-width 면 단일/메이슨리에서는 의도한 상한으로 동작하고, 셀 안에서는 셀에 맞춰 줄어든다.
    if (this.options.imageSize && finalSizeConfig.width) {
      img.style.maxWidth = finalSizeConfig.width + 'px';
    }

    // 터치 영역 확보는 CSS(product-description-image-manager.css)가 레이아웃별로 판단한다.
    // 여기서 인라인 min-width/min-height 200px 를 박으면 그리드 트랙(모바일 80~120px)과
    // 열 폭(≈172px)을 넘겨 이웃 이미지 위로 겹쳐 그려진다. 인라인이라 CSS 로 되돌릴 수도 없다.

    // 비율 및 비정상 비율(극단적 세로/가로 비율: 0.75 미만 또는 1.33 초과) 가드
    const rawRatio = (imageData.width && imageData.height) ? (imageData.width / imageData.height) : null;
    const isExtremeRatio = rawRatio !== null && (rawRatio < 0.75 || rawRatio > 1.33);

    if (isExtremeRatio) {
      img.classList.add('pdim-image--extreme-ratio');
      wrapper.classList.add('pdim-image-wrapper--extreme-ratio');
    }

    // 비동기 이미지 로드 후에도 실제 비율 검증 (Shopify legacy 이미지 등 width/height 부재 시 대응)
    img.addEventListener('load', () => {
      if (img.naturalWidth && img.naturalHeight) {
        const naturalRatio = img.naturalWidth / img.naturalHeight;
        if (naturalRatio < 0.75 || naturalRatio > 1.33) {
          img.classList.add('pdim-image--extreme-ratio');
          wrapper.classList.add('pdim-image-wrapper--extreme-ratio');
          img.style.objectFit = 'contain';
        }
      }
    }, { once: true });

    // 비율 설정 (정적 상수 활용)
    // 1) 콜라주 레이아웃은 셀 구조에 맞추고 잘림/과도한 확대를 방지하기 위해 contain 적용
    // 2) 극단적 비율 이미지인 경우에도 잘림 방지를 위해 contain 강제 적용
    // 3) 그 외 일반 비율 이미지는 설정된 imageRatio 적용
    if (this.options.layout === 'collage' || isExtremeRatio) {
      img.style.objectFit = 'contain';
    } else if (this.options.imageRatio !== 'auto') {
      img.style.aspectRatio = ProductDescriptionImageManager.ASPECT_RATIO_CSS[this.options.imageRatio] || 'auto';
      img.style.objectFit = 'cover';
    }
    
    // 모바일 최적화: 터치 디바이스에서 hover 효과 제거
    if (this.isTouchDevice) {
      wrapper.classList.add('pdim-touch-device');
    }
    
    wrapper.appendChild(img);
    
    // Lightbox: 디바이스와 무관하게 설정(enableLightbox)만 따른다.
    // 예전엔 `!this.isMobile` 조건 탓에 모바일에서는 클릭 핸들러가 아예 붙지 않았다.
    // checkMobileDevice() 는 '작은 화면 OR 터치 OR 모바일 UA' 라 폭 768px 이하 데스크톱 창까지
    // 모바일로 판정하므로, 실질적으로 팝업이 죽는 범위가 폰보다 훨씬 넓었다.
    if (this.options.enableLightbox) {
      wrapper.style.cursor = 'pointer';
      wrapper.addEventListener('click', () => {
        this.openLightbox(imageData.src, imageData.alt);
      });
    }
    
    // 캡션
    if (this.options.showCaption && imageData.alt) {
      const caption = document.createElement('div');
      caption.className = 'pdim-caption';
      caption.textContent = imageData.alt;
      wrapper.appendChild(caption);
    }
    
    return wrapper;
  }
  
  /**
   * Lazy loading 설정 (모바일 최적화)
   */
  setupLazyLoading(img) {
    if (!this.options.lazyLoadImages || !this.isMobile) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const targetImg = entry.target;
          const realSrc = targetImg.getAttribute('data-src');
          if (realSrc) {
            targetImg.src = realSrc;
            targetImg.removeAttribute('data-src');
            observer.unobserve(targetImg);
          }
        }
      });
    }, {
      rootMargin: '50px' // 화면에 50px 전에 로드 시작
    });
    
    observer.observe(img);
  }

  /**
   * Lightbox 열기 (메모리 누수 방지 개선)
   */
  openLightbox(src, alt) {
    // 이미 열린 lightbox가 있으면 먼저 제거
    const existingLightbox = document.querySelector('.pdim-lightbox');
    if (existingLightbox) existingLightbox.remove();
    
    const lightbox = document.createElement('div');
    lightbox.className = 'pdim-lightbox';
    lightbox.innerHTML = `
      <div class="pdim-lightbox-backdrop"></div>
      <div class="pdim-lightbox-content">
        <button type="button"
                class="theme-lightbox__toggle theme-lightbox__toggle--on-dark modal-close-animation pdim-lightbox-close"
                aria-label="${CLOSE_LABEL}">${CLOSE_ICON}</button>
        <img src="${src}" alt="${alt || ''}" class="pdim-lightbox-image">
      </div>
    `;
    
    document.body.appendChild(lightbox);
    
    // 닫기 핸들러 (이벤트 정리 포함)
    const escHandler = (e) => {
      if (e.key === 'Escape') close();
    };
    
    const close = () => {
      document.removeEventListener('keydown', escHandler);
      lightbox.remove();
      document.body.style.overflow = '';
    };
    
    lightbox.querySelector('.pdim-lightbox-close').addEventListener('click', close, { once: true });
    lightbox.querySelector('.pdim-lightbox-backdrop').addEventListener('click', close, { once: true });
    document.addEventListener('keydown', escHandler);
    
    document.body.style.overflow = 'hidden';
    
    // 애니메이션. .pdim-lightbox 는 opacity:0 이라 active 클래스가 붙기 전엔 '보이지 않는데
    // 스크롤만 잠긴' 상태다 — rAF 가 지연되는 상황(비활성 탭 등)에서 팝업이 안 열린 것처럼 보인다.
    // 타이머로 한 번 더 보정한다(classList.add 는 멱등).
    const reveal = () => lightbox.classList.add('pdim-lightbox-active');
    requestAnimationFrame(reveal);
    setTimeout(reveal, 50);
  }

  /**
   * 알림 표시 (접근성 및 UX 개선)
   */
  showNotification(message, type = 'info', duration = 3000) {
    // 기존 알림 제거
    const existingNotifications = document.querySelectorAll('.product-description-cleaner-notification');
    existingNotifications.forEach(notification => notification.remove());

    const notification = document.createElement('div');
    notification.className = `product-description-cleaner-notification pdim-notification-${type}`;
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');

    // 아이콘 추가 (접근성 향상)
    const icon = this.getNotificationIcon(type);
    const content = document.createElement('div');
    content.className = 'pdim-notification-content';

    content.innerHTML = `
      <span class="pdim-notification-icon" aria-hidden="true">${icon}</span>
      <span class="pdim-notification-text">${message}</span>
    `;

    notification.appendChild(content);

    // 닫기 버튼 (접근성)
    const closeButton = document.createElement('button');
    closeButton.className = 'pdim-notification-close';
    closeButton.setAttribute('aria-label', CLOSE_LABEL);
    closeButton.innerHTML = CLOSE_ICON;
    closeButton.addEventListener('click', () => {
      notification.remove();
    });
    notification.appendChild(closeButton);

    // 스타일 설정
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${this.getNotificationColor(type)};
      color: #fff;
      padding: 16px 20px;
      border-radius: 8px;
      z-index: var(--z-notification, 300);
      font-size: 14px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      border: none;
      cursor: default;
      max-width: 400px;
      animation: pdimSlideIn 0.3s ease-out;
      display: flex;
      align-items: center;
      gap: 12px;
    `;

    // 애니메이션 스타일 추가
    if (!document.getElementById('pdim-notification-styles')) {
      const style = document.createElement('style');
      style.id = 'pdim-notification-styles';
      style.textContent = `
        @keyframes pdimSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        .pdim-notification-close {
          background: none;
          border: none;
          color: #fff;
          cursor: pointer;
          padding: 0;
          margin-left: 8px;
          opacity: 0.8;
          transition: opacity 0.2s;
          display: flex;
          align-items: center;
        }
        /* 아이콘은 assets/icon-close.svg 사본(CLOSE_ICON). path 의 fill="black" 을 덮어야 한다. */
        .pdim-notification-close svg { width: 16px; height: 16px; display: block; }
        .pdim-notification-close svg path { fill: #fff; }
        .pdim-notification-close:hover {
          opacity: 1;
        }
        .pdim-notification-icon {
          font-size: 18px;
        }
        .pdim-notification-content {
          display: flex;
          align-items: center;
          gap: 8px;
          flex: 1;
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    // 자동 제거 (접근성 고려하여 수동 닫기 옵션 유지)
    if (duration > 0) {
      setTimeout(() => {
        if (notification.parentNode) {
          notification.style.animation = 'pdimSlideOut 0.3s ease-in';
          setTimeout(() => notification.remove(), 300);
        }
      }, duration);
    }

    // ESC 키로 닫기
    const escHandler = (e) => {
      if (e.key === 'Escape') {
        notification.remove();
        document.removeEventListener('keydown', escHandler);
      }
    };
    document.addEventListener('keydown', escHandler);
  }

  /**
   * 알림 타입별 아이콘
   */
  getNotificationIcon(type) {
    const icons = {
      success: '✅',
      error: '❌',
      warning: '⚠️',
      info: 'ℹ️'
    };
    return icons[type] || icons.info;
  }

  /**
   * 알림 타입별 색상
   */
  getNotificationColor(type) {
    const colors = {
      success: '#10b981',  // green-500
      error: '#ef4444',    // red-500
      warning: '#f59e0b',  // amber-500
      info: '#3b82f6'      // blue-500
    };
    return colors[type] || colors.info;
  }

  /**
   * 조건부 로그 출력 (디버그 모드에서만)
   */
  log(...args) {
    if (this.options.debug) {
      console.log('[PDIM]', ...args);
    }
  }

  /**
   * 리소스 정리 (메모리 관리 및 정리 개선)
   */
  destroy() {
    // 처리 중단 플래그 설정
    this.processingState.isProcessing = false;

    // MutationObserver 정리
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // 열린 Lightbox 정리
    const lightbox = document.querySelector('.pdim-lightbox');
    if (lightbox) {
      lightbox.remove();
      document.body.style.overflow = '';
    }

    // 캐시 정리 (메모리 최적화)
    this.clearCaches();

    // 이벤트 리스너 정리 (약한 참조 사용으로 자동 정리)
    // DOM 요소 참조 제거
    this.productImages = [];
    this.productData = null;

    // 전역 참조 정리
    if (window.productDescriptionImageManager === this) {
      window.productDescriptionImageManager = null;
    }

    this.log('🧹 PDIM 리소스 정리 완료');
  }

  /**
   * 캐시 정리 (메모리 관리)
   */
  clearCaches() {
    // URL 캐시 정리
    this.urlCache.clear();

    // 도메인 캐시 정리
    this.domainCache.clear();

    // 처리 상태 초기화
    this.processingState = {
      isProcessing: false,
      processedElements: 0,
      totalElements: 0,
      startTime: null
    };

    this.log('🗑️ 캐시 정리 완료');
  }

  /**
   * 메모리 사용량 모니터링 (디버그용)
   */
  getMemoryUsage() {
    if (performance.memory) {
      const mem = performance.memory;
      return {
        used: Math.round(mem.usedJSHeapSize / 1024 / 1024),
        total: Math.round(mem.totalJSHeapSize / 1024 / 1024),
        limit: Math.round(mem.jsHeapSizeLimit / 1024 / 1024)
      };
    }
    return null;
  }

  /**
   * 긴급 메모리 정리 (메모리 부족 시 호출)
   */
  emergencyCleanup() {
    this.log('🚨 긴급 메모리 정리 시작');

    // 캐시 완전 정리
    this.clearCaches();

    // 상품 이미지 데이터 정리
    this.productImages = this.productImages.slice(0, 3); // 최소한만 유지

    // 옵저버 정리
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // 강제 가비지 컬렉션 힌트 (지원 브라우저에서만)
    if (window.gc) {
      window.gc();
    }

    this.log('✅ 긴급 메모리 정리 완료');
  }
}

// 클래스를 전역에 등록 (중복 체크용)
window.ProductDescriptionImageManager = ProductDescriptionImageManager;

// 자동 실행 (페이지 로드 시)
if (typeof window !== 'undefined') {
  // 즉시 실행 함수로 확실한 초기화
  (function() {
    // 이미 초기화되었으면 중단
    if (window.productDescriptionImageManager) {
      console.log('[PDIM] Already initialized, skipping');
      return;
    }
    
    // URL 파라미터로 디버그 모드 제어
    const urlParams = new URLSearchParams(window.location.search);
    const isDebugMode = urlParams.has('debug') || window.location.hostname === 'localhost';
    
    // 조건부 로깅 헬퍼
    const debugLog = (...args) => isDebugMode && console.log(...args);
    
    debugLog('🚀 Product Description Image Manager 로드 완료');
    
    // CSS 자동 로드 - Shopify 에셋 경로 수정
    if (!document.querySelector('#pdim-styles')) {
      const link = document.createElement('link');
      link.id = 'pdim-styles';
      link.rel = 'stylesheet';
      // Shopify 에셋 URL 형식으로 수정 (theme.liquid에서 로드하는 것이 더 안전)
      // 에셋이 없으면 오류 무시
      link.onerror = () => {
        debugLog('[PDIM] CSS 파일 로드 실패 - 인라인 스타일 사용');
        link.remove();
      };
      // 정확한 Shopify 에셋 경로 사용
      const shopifyRoot = window.Shopify?.routes?.root || '/';
      link.href = `${shopifyRoot}cdn/shop/t/${window.Shopify?.theme?.id || ''}/assets/product-description-image-manager.css`;
      document.head.appendChild(link);
    }
    
    // 기본 설정으로 자동 실행
    // Liquid 템플릿에서 전달된 설정 우선 사용
    let defaultSettings = { ...ProductDescriptionImageManager.DEFAULT_SETTINGS };
    
    // 디버그 모드 설정
    defaultSettings.debug = isDebugMode;
    defaultSettings.showRemovedCount = isDebugMode;
    
    // Liquid 템플릿에서 전달된 설정과 병합 (Liquid 설정 우선)
    if (window.pdimSectionSettings) {
      // 첫 번째 섹션 설정 사용 (여러 섹션이 있을 경우)
      const sectionKeys = Object.keys(window.pdimSectionSettings);
      if (sectionKeys.length > 0) {
        const liquidSettings = window.pdimSectionSettings[sectionKeys[0]];
        // schema id → 옵션 이름 정규화 (별칭 포함)
        const normalized = ProductDescriptionImageManager.normalizeSettings(liquidSettings);
        defaultSettings = { ...defaultSettings, ...normalized };
        // 이 설정으로 초기화했음을 각인. 섹션 인라인 스크립트가 같은 설정으로 매니저를
        // 다시 만들지 않도록(= 상품 JSON 재요청·이미지 재삽입 방지) 하는 표식이다.
        window.pdimAppliedSignature = JSON.stringify(liquidSettings);
        debugLog('📋 Liquid 템플릿 설정 적용:', normalized);
      }
    }
    
    window.productDescriptionImageManager = new ProductDescriptionImageManager(defaultSettings);
    
    if (isDebugMode) {
      debugLog('✅ Image Manager 디버그 모드로 실행 중');
      debugLog('💡 프로덕션 모드로 전환하려면 URL에서 ?debug 제거');
      debugLog('⚙️ window.updatePDIMSettings({...})로 설정 변경 가능');
    }
    
    // 전역 설정 변경 API
    window.updatePDIMSettings = function(newSettings) {
      const debug = window.pdimLastOptions?.debug;
      if (debug) {
        console.log('🔄 설정 업데이트:', newSettings);
      }
      
      // 기존 컨테이너 및 매니저 제거
      document.querySelectorAll('.pdim-product-images').forEach(el => el.remove());
      window.productDescriptionImageManager?.destroy();
      
      // 새 설정으로 재시작
      const mergedSettings = { ...window.pdimLastOptions, ...newSettings };
      window.productDescriptionImageManager = new ProductDescriptionImageManager(mergedSettings);
      
      if (debug) console.log('✅ 설정 업데이트 완료');
    };
    
  })();
}

} // 중복 로드 방지 if문 닫기
